<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import api from '@/services/api.js';
import Swal from 'sweetalert2';

// --- ESTADOS ---
const cargando = ref(false);
const activeTab = ref('regionales');
const mostrarModal = ref(false);
const editMode = ref(false);
const itemIdActual = ref(null);

// Estados de Filtro Inteligente
const filtroBusqueda = ref('');
const filtroGrupoContable = ref('');

// Almacén de datos original
const datos = reactive({
  regionales: [],
  gerencias: [],
  secciones: [], // NUEVO: Almacén de secciones
  cargos: [],
  ubicaciones: [],
  gruposContables: [],
  tiposActivos: []
});

// Definición de pestañas (AÑADIDA SECCIONES)
const tabs = [
  { id: 'regionales', titulo: 'Regionales', icono: 'fas fa-map-marker-alt' },
  { id: 'gerencias', titulo: 'Gerencias', icono: 'fas fa-sitemap' },
  { id: 'secciones', titulo: 'Secciones', icono: 'fas fa-layer-group' }, // NUEVO
  { id: 'cargos', titulo: 'Cargos', icono: 'fas fa-id-badge' },
  { id: 'ubicaciones', titulo: 'Ubicaciones', icono: 'fas fa-building' },
  { id: 'gruposContables', titulo: 'Grupos Contables', icono: 'fas fa-calculator' },
  { id: 'tiposActivos', titulo: 'Tipos de Activos', icono: 'fas fa-laptop' }
];

// Formulario reactivo
const form = reactive({
  nombre: '',
  nombre_sala: '',
  regional: '',
  gerencia: '',
  seccion: '', // NUEVO: Campo para el formulario
  grupo_contable: '',
  vida_util_anos: 0,
  coeficiente_depreciacion: 0.0,
  tiene_mantenimiento_predictivo: false,
  altitud: null,
  temperatura_avg: null,
  humedad_avg: null
});

// --- PROPIEDAD COMPUTADA: FILTROS EN TIEMPO REAL ---
const datosFiltrados = computed(() => {
  let resultado = datos[activeTab.value] || [];

  // 1. Filtro por Grupo Contable (Solo en Tipos de Activos)
  if (activeTab.value === 'tiposActivos' && filtroGrupoContable.value) {
    resultado = resultado.filter(item => item.grupo_contable === filtroGrupoContable.value);
  }

  // 2. Filtro de Búsqueda de Texto Libre
  if (filtroBusqueda.value.trim() !== '') {
    const busqueda = filtroBusqueda.value.toLowerCase();
    resultado = resultado.filter(item => {
      const nombre = (item.nombre || item.nombre_sala || '').toLowerCase();
      return nombre.includes(busqueda);
    });
  }

  return resultado;
});

const cambiarPestana = (tabId) => {
  activeTab.value = tabId;
  filtroBusqueda.value = ''; // Limpiamos buscador
  filtroGrupoContable.value = ''; // Limpiamos selector
};

// --- FUNCIONES DE CARGA ---
const cargarTodo = async () => {
  cargando.value = true;
  try {
    // NUEVO: Añadimos la petición a 'secciones/'
    const [resReg, resGer, resSec, resCar, resUbi, resGru, resTip] = await Promise.all([
      api.get('regionales/'),
      api.get('gerencias/'),
      api.get('secciones/'), // NUEVO
      api.get('cargos/'),
      api.get('ubicaciones-admin/'), 
      api.get('grupos-contables/'),
      api.get('tipos-activos/')
    ]);
    
    datos.regionales = resReg.data.results || resReg.data;
    datos.gerencias = resGer.data.results || resGer.data;
    datos.secciones = resSec.data.results || resSec.data; // NUEVO
    datos.cargos = resCar.data.results || resCar.data;
    datos.ubicaciones = resUbi.data.results || resUbi.data;
    datos.gruposContables = resGru.data.results || resGru.data;
    datos.tiposActivos = resTip.data.results || resTip.data;
  } catch (error) {
    Swal.fire("Error", "No se pudieron cargar las configuraciones", "error");
  } finally {
    cargando.value = false;
  }
};

onMounted(cargarTodo);

// --- FUNCIONES DEL MODAL ---
const abrirModal = (modo, item = null) => {
  editMode.value = modo === 'editar';
  
  Object.keys(form).forEach(key => {
    if (typeof form[key] === 'boolean') form[key] = false;
    else if (typeof form[key] === 'number') form[key] = 0;
    else form[key] = '';
  });
  
  form.altitud = null;
  form.temperatura_avg = null;
  form.humedad_avg = null;

  if (editMode.value && item) {
    itemIdActual.value = item.id;
    Object.assign(form, item);
  } else {
    itemIdActual.value = null;
  }
  
  mostrarModal.value = true;
};

const cerrarModal = () => {
  mostrarModal.value = false;
};

// --- CRUD ---
const endpoints = {
  regionales: 'regionales/',
  gerencias: 'gerencias/',
  secciones: 'secciones/', // NUEVO
  cargos: 'cargos/',
  ubicaciones: 'ubicaciones-admin/',
  gruposContables: 'grupos-contables/',
  tiposActivos: 'tipos-activos/'
};

const guardarItem = async () => {
  cargando.value = true;
  const endpoint = endpoints[activeTab.value];
  const payload = {};
  
  if (activeTab.value === 'regionales') {
    payload.nombre = form.nombre.toUpperCase();
  } else if (activeTab.value === 'gerencias') {
    payload.nombre = form.nombre.toUpperCase();
    payload.regional = form.regional;
  } else if (activeTab.value === 'secciones') { // NUEVO
    payload.nombre = form.nombre.toUpperCase();
    payload.gerencia = form.gerencia;
  } else if (activeTab.value === 'cargos') {
    payload.nombre = form.nombre.toUpperCase();
    payload.seccion = form.seccion; // CAMBIO: Ahora el cargo depende de la sección
  } else if (activeTab.value === 'ubicaciones') {
    payload.nombre_sala = form.nombre_sala.toUpperCase();
    payload.regional = form.regional;
    payload.altitud = form.altitud || null;
    payload.temperatura_avg = form.temperatura_avg || null;
    payload.humedad_avg = form.humedad_avg || null;
  } else if (activeTab.value === 'gruposContables') {
    payload.nombre = form.nombre.toUpperCase();
    payload.vida_util_anos = form.vida_util_anos;
    payload.coeficiente_depreciacion = form.coeficiente_depreciacion;
    payload.tiene_mantenimiento_predictivo = form.tiene_mantenimiento_predictivo;
  } else if (activeTab.value === 'tiposActivos') {
    payload.nombre = form.nombre.toUpperCase();
    payload.grupo_contable = form.grupo_contable;
  }

  try {
    if (editMode.value) {
      await api.put(`${endpoint}${itemIdActual.value}/`, payload);
      Swal.fire("Actualizado", "Registro actualizado correctamente", "success");
    } else {
      await api.post(endpoint, payload);
      Swal.fire("Creado", "Registro creado correctamente", "success");
    }
    cerrarModal();
    cargarTodo(); 
  } catch (error) {
    console.error("Error de API:", error.response?.data);
    Swal.fire("Error", "Verifique los datos ingresados", "error");
  } finally {
    cargando.value = false;
  }
};

const eliminarItem = async (id) => {
  const result = await Swal.fire({
    title: '¿Está seguro?',
    text: "Esta acción no se puede deshacer.",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Sí, eliminar'
  });

  if (result.isConfirmed) {
    cargando.value = true;
    try {
      await api.delete(`${endpoints[activeTab.value]}${id}/`);
      Swal.fire('Eliminado', 'El registro ha sido eliminado.', 'success');
      cargarTodo();
    } catch (error) {
      Swal.fire('Error', 'No se puede eliminar porque está en uso.', 'error');
    } finally {
      cargando.value = false;
    }
  }
};
</script>

<template>
  <div class="py-4 app-bg-transition">
    <div class="card card-custom border-0 shadow-lg">
      
      <div class="header-tamep px-4 py-3 text-white border-radius-top">
        <h5 class="mb-0 text-white font-weight-bolder">Panel de Control Maestro</h5>
        <small class="opacity-8">Administración de Jerarquías, Ubicaciones y Categorías</small>
      </div>

      <ul class="nav nav-tabs custom-tabs px-4" role="tablist">
        <li class="nav-item" v-for="tab in tabs" :key="tab.id">
          <a class="nav-link" :class="{ active: activeTab === tab.id }" 
             @click.prevent="cambiarPestana(tab.id)" href="#">
            <i :class="tab.icono" class="me-2"></i>{{ tab.titulo }}
          </a>
        </li>
      </ul>

      <div class="p-4">
        <div class="row mb-4 align-items-center">
          <div class="col-md-3">
            <h6 class="text-azul-tamep text-uppercase font-weight-bold mb-0 title-custom">
              Gestión de {{ tabs.find(t => t.id === activeTab).titulo }}
            </h6>
          </div>
          
          <div class="col-md-9 d-flex justify-content-end align-items-center gap-3">
            <div class="input-group input-group-sm w-30">
              <span class="input-group-text bg-input-custom border-custom text-body"><i class="fas fa-search"></i></span>
              <input type="text" v-model="filtroBusqueda" class="form-control bg-input-custom border-custom text-custom" placeholder="Buscar por nombre...">
            </div>

            <div v-if="activeTab === 'tiposActivos'" class="w-30">
              <select v-model="filtroGrupoContable" class="form-select form-select-sm bg-input-custom border-custom text-custom" style="border-radius: 6px;">
                <option value="">Todos los Grupos Contables</option>
                <option v-for="gc in datos.gruposContables" :key="gc.id" :value="gc.id">{{ gc.nombre }}</option>
              </select>
            </div>

            <button @click="abrirModal('nuevo')" class="btn-tamep-primary px-4 mb-0 text-sm py-2">
              <i class="fas fa-plus me-2"></i> Nuevo
            </button>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table align-items-center mb-0 text-sm table-custom">
            <thead class="bg-table-header text-secondary">
              <tr>
                <th class="text-uppercase font-weight-bolder opacity-7">Descripción Principal</th>
                <th class="text-uppercase font-weight-bolder opacity-7" v-if="activeTab !== 'regionales'">Detalles Especiales</th>
                <th class="text-uppercase font-weight-bolder opacity-7 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="cargando"><td colspan="3" class="text-center py-4 text-custom">Cargando datos...</td></tr>
              <tr v-else-if="datosFiltrados.length === 0"><td colspan="3" class="text-center py-4 text-muted">No se encontraron registros.</td></tr>
              
              <tr v-for="item in datosFiltrados" :key="item.id" class="tr-custom">
                <td class="px-4">
                  <span class="font-weight-bold text-dark-custom">
                    {{ item.nombre || item.nombre_sala }}
                  </span>
                </td>
                
                <td v-if="activeTab !== 'regionales'">
                  <span v-if="activeTab === 'gerencias'" class="badge bg-info">{{ item.regional_nombre || item.regional }}</span>
                  <span v-if="activeTab === 'secciones'" class="badge" style="background-color: #6f42c1;">{{ item.gerencia_nombre || item.gerencia }}</span>
                  <span v-if="activeTab === 'cargos'" class="badge bg-secondary">{{ item.seccion_nombre || item.seccion }}</span>
                  <span v-if="activeTab === 'tiposActivos'" class="badge bg-dark">{{ item.grupo_nombre || item.grupo_contable }}</span>
                  
                  <div v-if="activeTab === 'ubicaciones'">
                    <span class="badge bg-primary mb-1">{{ item.regional_nombre || 'Regional' }}</span><br>
                    <small class="text-muted" v-if="item.temperatura_avg">
                      Temp: {{ item.temperatura_avg }}°C | Hum: {{ item.humedad_avg }}% | Alt: {{ item.altitud }}m
                    </small>
                  </div>
                  
                  <div v-if="activeTab === 'gruposContables'">
                    <small class="text-custom">Vida Útil: {{ item.vida_util_anos }} años | Deprec: {{ item.coeficiente_depreciacion }}%</small><br>
                    <span :class="item.tiene_mantenimiento_predictivo ? 'text-success' : 'text-danger'">
                      <i :class="item.tiene_mantenimiento_predictivo ? 'fas fa-robot' : 'fas fa-times-circle'"></i> 
                      {{ item.tiene_mantenimiento_predictivo ? 'IA Activada' : 'Sin IA' }}
                    </span>
                  </div>
                </td>

                <td class="text-center">
                  <button @click="abrirModal('editar', item)" class="btn btn-link text-info px-2 mb-0"><i class="fas fa-edit fa-lg"></i></button>
                  <button @click="eliminarItem(item.id)" class="btn btn-link text-danger px-2 mb-0"><i class="fas fa-trash fa-lg"></i></button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="mostrarModal" class="tamep-modal-overlay">
      <div class="tamep-modal-content p-4 animate__animated animate__fadeInDown">
        <div class="modal-header-tamep mb-4 text-white text-center p-3 rounded-3 shadow-sm">
          <h6 class="mb-0 text-white font-weight-bold">
            {{ editMode ? 'Editar' : 'Nuevo' }} Registro
          </h6>
        </div>

        <form @submit.prevent="guardarItem">
          <div v-if="activeTab === 'regionales'">
            <label class="label-custom">Nombre de la Regional</label>
            <input type="text" v-model="form.nombre" class="form-select-custom mb-3" required />
          </div>

          <div v-if="activeTab === 'gerencias'">
            <label class="label-custom">Nombre de la Gerencia</label>
            <input type="text" v-model="form.nombre" class="form-select-custom mb-3" required />
            <label class="label-custom">Pertenece a la Regional</label>
            <select v-model="form.regional" class="form-select-custom mb-3" required>
              <option v-for="r in datos.regionales" :key="r.id" :value="r.id">{{ r.nombre }}</option>
            </select>
          </div>

          <div v-if="activeTab === 'secciones'">
            <label class="label-custom">Nombre de la Sección</label>
            <input type="text" v-model="form.nombre" class="form-select-custom mb-3" required />
            <label class="label-custom">Pertenece a la Gerencia</label>
            <select v-model="form.gerencia" class="form-select-custom mb-3" required>
              <option v-for="g in datos.gerencias" :key="g.id" :value="g.id">{{ g.nombre }} ({{ g.regional_nombre }})</option>
            </select>
          </div>

          <div v-if="activeTab === 'cargos'">
            <label class="label-custom">Nombre del Cargo</label>
            <input type="text" v-model="form.nombre" class="form-select-custom mb-3" required />
            <label class="label-custom">Pertenece a la Sección</label> <select v-model="form.seccion" class="form-select-custom mb-3" required>
              <option v-for="s in datos.secciones" :key="s.id" :value="s.id">{{ s.nombre }} ({{ s.gerencia_nombre }})</option>
            </select>
          </div>

          <div v-if="activeTab === 'ubicaciones'">
            <label class="label-custom">Nombre de la Sala / Oficina</label>
            <input type="text" v-model="form.nombre_sala" class="form-select-custom mb-3" required />
            <label class="label-custom">Regional</label>
            <select v-model="form.regional" class="form-select-custom mb-3" required>
              <option v-for="r in datos.regionales" :key="r.id" :value="r.id">{{ r.nombre }}</option>
            </select>
            
            <div class="row">
              <div class="col-4">
                <label class="label-custom" title="Metros sobre nivel del mar">Altitud (m)</label>
                <input type="number" v-model="form.altitud" class="form-select-custom mb-3" placeholder="Ej: 3600" />
              </div>
              <div class="col-4">
                <label class="label-custom">Temp. Prom (°C)</label>
                <input type="number" step="0.01" v-model="form.temperatura_avg" class="form-select-custom mb-3" placeholder="Ej: 15.5" />
              </div>
              <div class="col-4">
                <label class="label-custom">Humedad (%)</label>
                <input type="number" step="0.01" v-model="form.humedad_avg" class="form-select-custom mb-3" placeholder="Ej: 45.0" />
              </div>
            </div>
            <small class="text-muted d-block mb-3" style="font-size: 10px;">* Los datos de altitud, temperatura y humedad son vitales para la IA.</small>
          </div>

          <div v-if="activeTab === 'gruposContables'">
            <label class="label-custom">Nombre del Grupo</label>
            <input type="text" v-model="form.nombre" class="form-select-custom mb-3" required />
            <div class="row">
              <div class="col-6">
                <label class="label-custom">Vida Útil (Años)</label>
                <input type="number" v-model="form.vida_util_anos" class="form-select-custom mb-3" required />
              </div>
              <div class="col-6">
                <label class="label-custom">Depreciación (%)</label>
                <input type="number" step="0.01" v-model="form.coeficiente_depreciacion" class="form-select-custom mb-3" required />
              </div>
            </div>
            <div class="form-check mt-2 mb-3">
              <input class="form-check-input" type="checkbox" v-model="form.tiene_mantenimiento_predictivo" id="checkIA">
              <label class="form-check-label font-weight-bold text-dark-custom" for="checkIA">
                Habilitar Mantenimiento Predictivo (IA)
              </label>
            </div>
          </div>

          <div v-if="activeTab === 'tiposActivos'">
            <label class="label-custom">Nombre del Tipo</label>
            <input type="text" v-model="form.nombre" class="form-select-custom mb-3" required />
            <label class="label-custom">Grupo Contable</label>
            <select v-model="form.grupo_contable" class="form-select-custom mb-3" required>
              <option v-for="gc in datos.gruposContables" :key="gc.id" :value="gc.id">{{ gc.nombre }}</option>
            </select>
          </div>

          <div class="d-flex gap-3 mt-4 pt-2">
            <button type="button" @click="cerrarModal" class="btn-tamep-secondary flex-grow-1 py-3">
              <i class="fas fa-times me-2"></i> Cancelar
            </button>
            <button type="submit" class="btn-tamep-primary flex-grow-1 py-3" :disabled="cargando">
              <i class="fas fa-save me-2"></i> {{ cargando ? 'Guardando...' : 'Guardar' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* VARIABLES BASE (CLARO) */
.app-bg-transition { transition: background-color 0.3s ease; }
.card-custom { background-color: #ffffff; transition: background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease; }
.text-custom { color: #333; transition: color 0.3s ease; }
.text-dark-custom { color: #344767; transition: color 0.3s ease; }
.bg-table-header { background-color: #f8f9fa; transition: background-color 0.3s ease; }
.bg-input-custom { background-color: #fff; transition: background-color 0.3s ease; }
.border-custom { border-color: #d2d6da; transition: border-color 0.3s ease; }
.tr-custom { border-bottom: 1px solid #e9ecef; transition: border-color 0.3s ease; }

/* ESTILOS TAMEP CORPORATIVO */
.header-tamep { background: linear-gradient(135deg, #003366 0%, #001d3d 100%); }
.text-azul-tamep { color: #003366; }
.border-radius-top { border-top-left-radius: 12px; border-top-right-radius: 12px; }

/* TABS ESTILIZADOS */
.custom-tabs { border-bottom: 2px solid #e9ecef; padding-top: 10px; transition: border-color 0.3s ease; }
.custom-tabs .nav-link { color: #8392ab; font-weight: 600; border: none; padding: 12px 20px; border-bottom: 3px solid transparent; transition: all 0.3s ease; }
.custom-tabs .nav-link:hover { color: #003366; }
.custom-tabs .nav-link.active { color: #003366; border-bottom-color: #003366; background: transparent; }

/* INPUTS Y BOTONES */
.label-custom { font-size: 0.70rem; font-weight: 800; text-transform: uppercase; color: #8392ab; margin-bottom: 5px; display: block; }
.form-select-custom { background-color: #fff; color: #333; display: block; width: 100%; padding: 0.65rem; font-size: 0.85rem; border: 1px solid #d2d6da; border-radius: 10px; outline: none; transition: all 0.3s ease; text-transform: uppercase; }
.form-select-custom:focus { border-color: #003366; box-shadow: 0 0 0 2px rgba(0,51,102,0.1); }
.btn-tamep-primary { background: #003366; color: white; border: none; border-radius: 10px; font-weight: 700; text-transform: uppercase; transition: all 0.3s ease; }
.btn-tamep-primary:hover:not(:disabled) { background: #001d3d; transform: translateY(-2px); box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
.btn-tamep-secondary { background: transparent; color: #8392ab; border: 2px solid #d2d6da; border-radius: 10px; font-weight: 700; text-transform: uppercase; transition: all 0.3s ease; }
.btn-tamep-secondary:hover:not(:disabled) { background: #f8f9fa; color: #344767; border-color: #344767; transform: translateY(-2px); box-shadow: 0 4px 6px rgba(0,0,0,0.1); }

/* MODAL OVERLAY */
.tamep-modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 29, 61, 0.7); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(4px); }
.tamep-modal-content { background: white; width: 95%; max-width: 500px; border-radius: 15px; box-shadow: 0 20px 40px rgba(0,0,0,0.4); transition: background-color 0.3s ease; }
.modal-header-tamep { background: #003366; }


/* =========================================
   SOPORTE PARA MODO OSCURO (DARK MODE)
   ========================================= */
</style>

<style>
.dark-version .card-custom, .dark-mode .card-custom { background-color: #1e1e1e !important; border: 1px solid #2d2d2d !important; box-shadow: 0 4px 15px rgba(0,0,0,0.3) !important;}
.dark-version .text-custom, .dark-mode .text-custom { color: #e0e0e0 !important; }
.dark-version .text-dark-custom, .dark-mode .text-dark-custom { color: #ffffff !important; }
.dark-version .bg-table-header, .dark-mode .bg-table-header { background-color: #2c2c2c !important; color: #aaa !important; }
.dark-version .tr-custom, .dark-mode .tr-custom { border-bottom-color: #333 !important; }
.dark-version .custom-tabs, .dark-mode .custom-tabs { border-bottom-color: #333 !important; }
.dark-version .custom-tabs .nav-link, .dark-mode .custom-tabs .nav-link { color: #a0a0a0 !important; }
.dark-version .custom-tabs .nav-link:hover, .dark-mode .custom-tabs .nav-link:hover { color: #4dabf7 !important; }
.dark-version .custom-tabs .nav-link.active, .dark-mode .custom-tabs .nav-link.active { color: #4dabf7 !important; border-bottom-color: #4dabf7 !important; }
.dark-version .title-custom, .dark-mode .title-custom { color: #4dabf7 !important; }

/* Formularios en Modo Oscuro */
.dark-version .bg-input-custom,
.dark-version .form-select-custom,
.dark-mode .bg-input-custom,
.dark-mode .form-select-custom { background-color: #2c2c2c !important; color: #e0e0e0 !important; border-color: #444 !important; }
.dark-version .form-select-custom:focus, .dark-mode .form-select-custom:focus { border-color: #4dabf7 !important; box-shadow: 0 0 0 2px rgba(77, 171, 247, 0.2) !important; }
.dark-version .input-group-text, .dark-mode .input-group-text { background-color: #2c2c2c !important; color: #aaa !important; border-color: #444 !important; }

/* Modal en Modo Oscuro */
.dark-version .tamep-modal-content, .dark-mode .tamep-modal-content { background-color: #1e1e1e !important; border: 1px solid #333 !important; }
.dark-version .btn-tamep-secondary, .dark-mode .btn-tamep-secondary { border-color: #444 !important; color: #aaa !important; }
.dark-version .btn-tamep-secondary:hover:not(:disabled), .dark-mode .btn-tamep-secondary:hover:not(:disabled) { background-color: #2c2c2c !important; color: #fff !important; border-color: #666 !important; }
</style>